import React, { useEffect, useState, useMemo } from "react";
import DataTable from "./ExpandedTable";
import { Button, Row, Col, Table } from "react-bootstrap";
import baseURL from "../../../../Url/NodeBaseURL";
import { FaChevronDown, FaChevronRight, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const InventoryReport = () => {
    const [products, setProducts] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ====================== Columns ======================
    const columns = React.useMemo(
        () => [
            {
                Header: "",
                id: "expander",
                Cell: ({ row }) => (
                    <span {...row.getToggleRowExpandedProps()}>
                        {row.isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                ),
            },
            {
                Header: "Sr. No.",
                Cell: ({ row }) => row.index + 1,
            },
            { Header: "Category Name", accessor: "product_name" },
            { Header: "Barcode", accessor: "rbarcode" },
            { Header: "Metal Type", accessor: "Category" },
            { Header: "Pur Qty", accessor: "pur_qty" },
            { Header: "Pur Weight", accessor: "pur_weight" },
            { Header: "Sale Qty", accessor: "sale_qty" },
            { Header: "Sale Weight", accessor: "sale_weight" },
            { Header: "Bal Qty", accessor: "bal_qty" },
            { Header: "Bal Weight", accessor: "bal_weight" },
        ],
        []
    );

    // ====================== Fetch Data ======================
useEffect(() => {
    const fetchData = async () => {
        try {
            const [prodRes, tagsRes] = await Promise.all([
                fetch(`${baseURL}/get/products`),
                fetch(`${baseURL}/get/opening-tags-entry`),
            ]);

            if (!prodRes.ok || !tagsRes.ok) {
                // If API fails → show table with "No data available"
                setProducts([]);
                setTags([]);
                return;
            }

            const prodData = await prodRes.json();
            const tagsData = await tagsRes.json();

            setProducts(prodData || []);
            setTags(tagsData.result || []);
        } catch (err) {
            // If fetch error → also show empty table instead of error
            setProducts([]);
            setTags([]);
        } finally {
            setLoading(false);
        }
    };

    fetchData();
}, []);


    // ====================== Excel Download Function ======================
    const downloadExcel = () => {
        if (!products.length) return;

        // Prepare main data for Excel - Stock Summary Sheet
        const stockSummaryData = products.map((product, index) => ({
            "Sr. No.": index + 1,
            "Category Name": product.product_name || "",
            "Barcode": product.rbarcode || "",
            "Metal Type": product.Category || "",
            "Pur Qty": product.pur_qty || 0,
            "Pur Weight": product.pur_weight || 0,
            "Sale Qty": product.sale_qty || 0,
            "Sale Weight": product.sale_weight || 0,
            "Bal Qty": product.bal_qty || 0,
            "Bal Weight": product.bal_weight || 0,
        }));

        // Prepare grouped tags data with product headers - Product Tags Details Sheet
        const productTagsData = [];
        
        products.forEach((product) => {
            const productTags = tags.filter(tag => tag.product_id === product.product_id);
            
            if (productTags.length > 0) {
                // Add product header section
                productTagsData.push({
                    "A": `Product: ${product.product_name || 'N/A'}`,
                    "B": `Barcode: ${product.rbarcode || 'N/A'}`,
                    "C": `Metal Type: ${product.Category || 'N/A'}`,
                    "D": "",
                    "E": "",
                    "F": "",
                    "G": ""
                });

                productTagsData.push({
                    "A": `Purchase Qty: ${product.pur_qty || 0}`,
                    "B": `Purchase Weight: ${product.pur_weight || 0}`,
                    "C": `Sale Qty: ${product.sale_qty || 0}`,
                    "D": `Sale Weight: ${product.sale_weight || 0}`,
                    "E": `Balance Qty: ${product.bal_qty || 0}`,
                    "F": `Balance Weight: ${product.bal_weight || 0}`,
                    "G": ""
                });

                // Add empty row for spacing
                productTagsData.push({});

                // Add column headers for tags
                productTagsData.push({
                    "A": "Tag ID",
                    "B": "Sub Category",
                    "C": "Barcode",
                    "D": "Gross Weight",
                    "E": "Stones Weight",
                    "F": "Net Weight",
                    "G": "Status"
                });

                // Add tag data
                productTags.forEach((tag, tagIndex) => {
                    productTagsData.push({
                        "A": tagIndex + 1,
                        "B": tag.sub_category || "-",
                        "C": tag.PCode_BarCode || "-",
                        "D": tag.Gross_Weight || 0,
                        "E": tag.Stones_Weight || 0,
                        "F": tag.TotalWeight_AW || 0,
                        "G": tag.Status || "",
                    });
                });

                // Add separator rows between products (3 empty rows)
                productTagsData.push({});
                productTagsData.push({});
                productTagsData.push({});
            }
        });

        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Add Stock Summary sheet
        const wsStockSummary = XLSX.utils.json_to_sheet(stockSummaryData);
        XLSX.utils.book_append_sheet(wb, wsStockSummary, "Stock Summary");

        // Add Product Tags Details sheet
        const wsProductTags = XLSX.utils.json_to_sheet(productTagsData, { skipHeader: true });

        // Set column widths for better formatting
        const colWidths = [
            { wch: 10 }, // A - Tag ID
            { wch: 15 }, // B - Sub Category
            { wch: 15 }, // C - Barcode
            { wch: 15 }, // D - Gross Weight
            { wch: 15 }, // E - Stones Weight
            { wch: 15 }, // F - Net Weight
            { wch: 15 }, // G - Status
        ];
        wsProductTags['!cols'] = colWidths;

        // Add some styling by merging cells for headers
        if (productTagsData.length > 0) {
            const merges = [];
            let currentRow = 0;

            products.forEach((product) => {
                const productTags = tags.filter(tag => tag.product_id === product.product_id);
                if (productTags.length > 0) {
                    // Merge cells for product header (row 1)
                    merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 6 } });
                    currentRow += 3; // Move to next product section (header + details + empty row)
                    
                    // Skip tag headers and data rows
                    currentRow += 1 + productTags.length + 3; // headers + tags + separator rows
                }
            });
            
            wsProductTags['!merges'] = merges;
        }

        XLSX.utils.book_append_sheet(wb, wsProductTags, "Product Tags Details");

        // Generate and download Excel file
        const fileName = `Inventory_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    // ====================== Expanded Sub Component ======================
    const renderRowSubComponent = ({ row }) => {
        const productId = row.original.product_id;
        const relatedTags = tags.filter((tag) => tag.product_id === productId);

        if (relatedTags.length === 0) {
            return (
                <div className="p-2 text-muted">
                    No tags found for this product.
                </div>
            );
        }

        return (
            <div className="p-3">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Tag ID</th>
                            <th>Sub Category</th>
                            <th>Barcode</th>
                            <th>Gross Weight</th>
                            <th>Stones Weight</th>
                            <th>Net Weight</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {relatedTags.map((tag, index) => (
                            <tr key={tag.opentag_id}>
                                <td>{index + 1}</td>
                                <td>{tag.sub_category || "-"}</td>
                                <td>{tag.PCode_BarCode || "-"}</td>
                                <td>{tag.Gross_Weight}</td>
                                <td>{tag.Stones_Weight}</td>
                                <td>{tag.TotalWeight_AW}</td>
                                <td>{tag.Status}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // ====================== Render ======================
    return (
        <div className="main-container">
            <div className="payments-table-container">
                <Row className="mb-3">
                    <Col className="d-flex justify-content-between align-items-center">
                        <h3>Inventory Report</h3>
                        <Button 
                            variant="success" 
                            onClick={downloadExcel}
                            disabled={loading || products.length === 0}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaFileExcel />
                            Download Excel
                        </Button>
                    </Col>
                </Row>

                {loading ? (
                    <p>Loading...</p>
                ) : error ? (
                    <p className="text-danger">{error}</p>
                ) : (
                    <DataTable
                        columns={columns}
                        data={[...products].reverse()}
                        renderRowSubComponent={renderRowSubComponent}
                    />
                )}
            </div>
        </div>
    );
};

export default InventoryReport;